<template>
    <div :class="chart_selected.data ? 'p-0 table-card play-table-view mb-3' : ''">
        <div v-if="chart_selected.data" class="position-relative multi-dimension-table-wrapper">
            <Transition name="fade" :duration="300">
                <div v-if="is_loading_dimension" class="loading-overlay">
                    <div role="status" class="d-inline-block spinner-border spinner-border-lg ms-2"><span class="visually-hidden">Loading</span></div>
                    <p>Loading...</p>
                </div>
            </Transition>
            <div>
                <div class="table-responsive">
                    <table class="table table-width-responsive table-with-pagination mb-0" style="min-width: 100%;">
                        <thead>
                            <tr class="table-header-row">
                                <th style="width:15%;" class="d-table-cell bg-white">
                                    <select class="ps-0 form-select form-select-sm border-0 bg-transparent p-0 m-0 fw-bold dimension-select" v-model="selected_dimension" @change="onDimensionChange" aria-label="Selected Dimension">
                                        <option v-for="dimension in dropdown_dimensions" :key="dimension.text" :value="dimension.text">{{ dimension.text }}</option>
                                    </select>
                                </th>

                                <th v-for="key in metricKeys" :key="key" class="d-table-cell bg-white" style="cursor: pointer;" @click="sortByKey(key)">
                                    <div class="d-flex flex-row">
                                        {{ key }}
                                        <div class="d-flex flex-column">
                                            <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === key && sort_order === 'asc' }" alt="">
                                            <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === key && sort_order === 'des' }" alt="">
                                        </div>
                                    </div>
                                </th>
                                <th v-if="if_on_report_page && !if_benchmark_report" class="d-table-cell bg-white" style="width: auto;">
                                    <!-- Filter column header -->
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(data, index) in table_data_shown" :key="data._rowId" 
                                :class="[`table-row-${index + 1}`, { 'grayscale-table-template-row': if_chart_filter_applied && data.label !== chart_filter_label }]">
                                <td class="d-table-cell align-start text-start">
                                    {{ data.label === null ? '(not set)' : data.label }}
                                </td>
                                <td v-for="key in metricKeys" :key="key" class="d-table-cell">
                                    {{ data[key] === null ? '(not set)' : applyFormatToCurrentValue(data[key], key) }}
                                </td>
                                <td v-if="if_on_report_page && !if_benchmark_report" class="d-table-cell">
                                    <button 
                                        class="btn btn-icon-only-xs btn-primary" 
                                        :disabled="applied_chart_filter.if_chart_filter_applied && !(if_chart_filter_applied && chart_filter_label === data.label)"
                                        @click="exploreData(data)" aria-label="Button">
                                        <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination moved outside of scrollable area -->
                <div v-if="table_data.length > 5" class="table-pagination my-2">
                <div class="table-pagination--group d-none d-md-flex">
                    <div class="position-relative">
                        <select class="form-select" name="num_per_page" id="num_per_page" v-model="num_per_page" @change="reloadPagination" aria-label="Num Per Page">
                            <option :value="5000">ALL</option>
                            <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                        </select>
                        <div class="select-display-text">
                            Showing {{ num_per_page === 5000 ? 'ALL' : num_per_page }}
                        </div>
                    </div>
                </div>

                <div class="table-pagination--group d-none d-lg-flex">
                    {{ table_data.length === 0 ? '0' : item_interval }} of {{ number_with_zero(table_data.length) }}
                </div>

                <div class="table-pagination--group">
                    <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage" :disabled="page_index === 0">
                        <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                    </button>

                    <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                        <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" 
                                :class="{ 'current-page': page === page_index + 1 }" 
                                @click="moveToPage(page)">
                            {{ page }}
                        </button>
                    </div>
                    <div class="d-flex page-selector" v-else>
                        <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" 
                                :class="{ 'current-page': page === page_index + 1 }" 
                                @click="moveToPage(page)">
                            {{ page }}
                        </button>

                        <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                        ...
                        </button>
                        <button v-if="page_index >= 2 && page_index < page_index_array.length - 1" class="btn btn-none btn-icon-only-md current-page">
                            {{ page_index + 1 }}
                        </button>

                        <button v-if="page_index < page_index_array.length - 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled aria-hidden="true">
                        ...
                        </button>
                        <button class="btn btn-none btn-icon-only-md" 
                                :class="{ 'current-page': page_index === page_index_array.length - 1 }" 
                                @click="moveToPage(page_index_array.length)"
                                :key="page_index_array.length">
                            {{ page_index_array.length }}
                        </button>
                    </div>

                    <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage" :disabled="page_index === page_count - 1">
                        <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                    </button>
                </div>
            </div>
            </div>
        </div>

        <div v-else-if="!if_refreshing" class="d-inline-block text-center">
            <h1 class="text-secondary mb-1"><i class="fas fa-chart-simple"></i></h1>
            <h4 class="text-secondary mb-2"><em>Press the <strong>Preview</strong> button to render the chart</em></h4>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import filtersMixin from '../../../../mixins/filters';

export default defineComponent({
    name: 'MultiDimensionTableTemplate',
    mixins: [filtersMixin],
    props: {
        chart_selected: {
            type: Object,
            required: true
        },
        id: {
            type: [String, Number],
            required: true
        },
        if_refreshing: {
            type: Boolean,
            default: false
        },
        if_on_report_page: {
            type: Boolean,
            default: false
        },
        if_benchmark_report: {
            type: Boolean,
            default: false
        },
        applied_chart_filter: {
            type: Object,
            default: () => ({})
        },
        section_id: {
            type: [String, Number],
            default: null
        },
        page_dimensions: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        effectiveSectionId() {
            return this.effectiveSectionId != null ? this.effectiveSectionId : this.id;
        },
        metricKeys() {
            if (!this.chart_selected.data) {
                return [];
            }
            const allKeys = Object.keys(this.chart_selected.data);
            const dimensionTexts = this.original_dimensions.map(d => d.text);
            return allKeys.filter(key => !dimensionTexts.includes(key));
        },
        dropdown_dimensions() {
            if (!this.original_dimensions.length) {
                return [];
            }
            // Columns filtered via chart-level filter click
            const chartFilterColumns = (this.applied_chart_filter?.chart_filters || []).map(f => f.column);
            // Columns filtered via active page-level dimension filters
            const pageFilterColumns = (this.page_dimensions || [])
                .filter(d => {
                    const val = d.value ?? d.default_value;
                    if (val === null || val === undefined || val === '') return false;
                    if (Array.isArray(val)) return val.length > 0;
                    return true;
                })
                .map(d => d.column);
            const excludedColumns = [...new Set([...chartFilterColumns, ...pageFilterColumns])];
            if (excludedColumns.length === 0) {
                return this.original_dimensions;
            }
            return this.original_dimensions.filter(d => !excludedColumns.includes(d.value));
        }
    },
    data() {
        return {
            chart: null,
            format_type: [],
            original_dimensions: [],
            selected_dimension: null,
            color: 'yellow',
            num_of_datasets: 0,
            if_chart_has_data: false,
            if_chart_filter_applied: false,
            chart_filter_label: null,
            is_loading_dimension: false,

            table_data: [],
            table_data_shown: [],
            sort_key: null,
            sort_order: 'asc',

            // pagination
            num_per_page: 15, // default to 15
            num_per_page_options: [5,10,15,20,25,30,50,100],
            page_count: null,
            page_index: 0,
            page_index_array: [],
            item_interval: ''
        };
    },
    watch:{
        chart_selected: {
            handler: function(newVal) {
                if (newVal.data) {
                    if(this.chart_selected.metric_formats){
                        this.format_type = this.chart_selected.metric_formats
                    } else {
                        this.format_type = ['number'];
                    }
                    this.if_chart_has_data = true;
                    // Check if a refetch for the correct dimension will fire;
                    // if so, skip loading the stale data so the user doesn't
                    // see the wrong dimension flash before the correct one loads.
                    if (this.reDrillIfNeeded() || this.restoreSelectedDimensionIfNeeded()) {
                        return;
                    }
                    this.loadTableData();
                    this.applyCurrentSort();
                }
            },
            deep: true,
        },
        if_refreshing:{
            handler: function(newVal, oldVal) {
                this.$forceUpdate();
            }
        },
        applied_chart_filter:{
            handler: function(newVal, oldVal) {
                // Determine if this table should reflect the chart filter
                // 1. If the filter was initiated from this chart/table ID
                // 2. If the filter dimension matches this table's primary dimension
                var isFromThisChart = this.id == this.applied_chart_filter.section_chart_id;
                var isDimensionMatch = false;

                if (this.applied_chart_filter.if_chart_filter_applied && this.applied_chart_filter.chart_filters && this.applied_chart_filter.chart_filters.length > 0) {
                    var filterColumn = this.applied_chart_filter.chart_filters[0].column;
                    var tableColumn = this.chart_selected.group_by && this.chart_selected.group_by.length > 0 
                        ? this.chart_selected.group_by[0].value 
                        : null;
                    
                    if (filterColumn && tableColumn && filterColumn === tableColumn) {
                        isDimensionMatch = true;
                    }
                }

                this.if_chart_filter_applied = this.applied_chart_filter.if_chart_filter_applied && (isFromThisChart || isDimensionMatch);
                
                // Update the highlight label
                if (this.if_chart_filter_applied && this.applied_chart_filter.chart_filters && this.applied_chart_filter.chart_filters.length > 0) {
                    this.chart_filter_label = this.applied_chart_filter.chart_filters[0].value;
                } else {
                    this.chart_filter_label = null;
                }

                this.reloadPagination();
                this.reDrillIfNeeded();
            }
        }
    },
    mounted() {
        if (this.chart_selected.data){
            // loop through metric_objects in chart to find format
            if(this.chart_selected.metric_formats){
                this.format_type = this.chart_selected.metric_formats
            } else {
                this.format_type = ['number'];
            }
            if(this.chart_selected.group_by){
                this.original_dimensions = [...this.chart_selected.group_by];
                this.selected_dimension = this.original_dimensions[0].text;
            }
            this.loadTableData();
            this.applyCurrentSort();
            this.if_chart_has_data = true;
        }
    },
    methods: {
        applyCurrentSort() {
            if (this.sort_key && this.table_data.length > 0 && Object.prototype.hasOwnProperty.call(this.table_data[0], this.sort_key)) {
                this.sortDataWithLabel();
            } else {
                this.reloadPagination();
            }
        },
        onDimensionChange(){
            const selectedDimensionObj = this.original_dimensions.find(d => d.text === this.selected_dimension);
            if (!selectedDimensionObj) {
                return;
            }

            this.is_loading_dimension = true;

            const chartPayload = {
                ...this.chart_selected,
                group_by: [selectedDimensionObj]
            };

            const updateText = (arr) => {
                if(arr && arr.length > 0) {
                    for(var i = 0; i < arr.length; i++){
                        if (arr[i].display_name) {
                            arr[i].text = arr[i].display_name;
                        } else if(arr[i].value && typeof arr[i].value === 'string') {
                            let split = arr[i].value.split('.');
                            let value = String(split[split.length-1]).replaceAll("_", " ");
                            arr[i].text = value.charAt(0).toUpperCase() + value.slice(1);
                        }
                    }
                }
            };

            updateText(chartPayload.group_by);
            updateText(chartPayload.metrics);

            const dimensionTextToShow = selectedDimensionObj.text;
            if (this.shouldBypassChartCache()) {
                chartPayload.bypass_cache = true;
            }
            window.axios.post('/api/chart/get_data', chartPayload)
                .then(response => {
                    this.chart_selected.data = response.data.data;
                    this.chart_selected.labels = response.data.labels;
                    this.chart_selected.metric_formats = response.data.formats;
                    this.chart_selected.group_by = [selectedDimensionObj];
                    this.loadTableData();
                    this.applyCurrentSort();
                    this.is_loading_dimension = false;
                    this.$nextTick(() => {
                        this.selected_dimension = dimensionTextToShow;
                    });
                })
                .catch(error => {
                    console.error('Error fetching dimension data:', error);
                    this.is_loading_dimension = false;
                });
        },
        restoreSelectedDimensionIfNeeded() {
            if (this.is_loading_dimension) return false;
            if (!this.chart_selected?.group_by?.length || !this.selected_dimension || !this.original_dimensions.length) return false;
            const currentDimText = this.chart_selected.group_by[0].text;
            if (currentDimText === this.selected_dimension) return false;
            const isFromThisChart = this.id == this.applied_chart_filter?.section_chart_id;
            if (this.applied_chart_filter?.if_chart_filter_applied && isFromThisChart) return false;
            const dimensionObj = this.original_dimensions.find(d => d.text === this.selected_dimension);
            if (!dimensionObj) return false;
            this.onDimensionChange();
            return true;
        },
        loadTableData(){
            if (!this.chart_selected.labels || !this.chart_selected.data) {
                this.table_data = [];
                return;
            }
            
            var table_data_temp =[];

            this.chart_selected.labels.forEach((value,index)=>{
                var data_obj = {
                    _rowId: index,
                    label: value
                }

                this.metricKeys.forEach((key) =>{
                    data_obj[key] = this.chart_selected.data[key][index];
                })
                table_data_temp.push(data_obj);

            })
            this.table_data = table_data_temp;
        },
        applyFormatToCurrentValue(data, key){
            if (key === '_rowId' || key === 'label'){
                return data;
            }
            
            if (!this.chart_selected.metrics || !this.chart_selected.group_by) {
                return data;
            }
            
            var index = this.chart_selected.metrics.findIndex(m => m.text == key);
            if (index == -1) {
                index = this.chart_selected.group_by.findIndex(m => m.text == key) - 1;
            } else if (this.chart_selected.type !== 'multi_dimension_table') {
                index += this.chart_selected.group_by.length - 1;
            }

            if (index < 0 || index >= this.format_type.length) {
                return data;
            }
            if(this.format_type[index] == 'money'){
                return this.currency_with_zero(data);
            }
            else if(this.format_type[index] == 'percent'){
                return this.percentage(data);
            }
            else if(this.format_type[index] == 'number'){
                return this.number_with_decimal(data);
            }
            else if(this.format_type[index] == 'date'){
                return this.date(data);
            }
            else {
                return data;
            }
        },
        sortByKey(key){
            if (this.sort_key != key){
                this.sort_key = key;
                this.sort_order = 'asc'; // reset to ascending sorting order
            } else{
                this.sort_order = (this.sort_order == 'asc'? 'des':'asc');
            }
            this.sortDataWithLabel();
        },
        sortDataWithLabel(){
            // Improved sorting that handles null/undefined values and different data types
            this.table_data.sort((a, b) => {
                let aVal = a[this.sort_key];
                let bVal = b[this.sort_key];
                
                // Handle null/undefined values - push them to the end
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;
                
                // Handle numeric values
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return this.sort_order === 'asc' ? aVal - bVal : bVal - aVal;
                }
                
                // Handle string comparison (case-insensitive)
                const aStr = String(aVal).toLowerCase();
                const bStr = String(bVal).toLowerCase();
                
                if (this.sort_order === 'asc') {
                    return aStr > bStr ? 1 : aStr < bStr ? -1 : 0;
                } else {
                    return aStr < bStr ? 1 : aStr > bStr ? -1 : 0;
                }
            });
            this.reloadPagination();
        },
        reloadPagination(resetPage = true){
            this.page_count = Math.ceil(this.table_data.length / this.num_per_page);
            
            // Reset to page 0 when data changes, sorting happens, or filter changes
            // But keep current page when just navigating
            if (resetPage) {
                this.page_index = 0;
            } else {
                // Ensure current page is still valid after data changes
                if (this.page_index >= this.page_count) {
                    this.page_index = Math.max(0, this.page_count - 1);
                }
            }
            
            this.page_index_array = Array.from({length: this.page_count}, (_, i) => i + 1);
            this.getItemInterval();
        },
        getItemInterval(){
            var from = this.page_index * this.num_per_page + 1;
            var to = (this.page_index +1) * this.num_per_page;
            if(to>this.table_data.length)
                to = this.table_data.length;
            if (from >= to)
                this.item_interval = this.number(to);
            else
                this.item_interval = this.number(from) + ' - ' + this.number(to);

            this.table_data_shown = this.table_data.slice(from-1, to);
        },
        moveToPrevPage(){
            this.page_index--;
            this.getItemInterval();
        },
        moveToPage(page){
            this.page_index = page-1;
            this.getItemInterval();
        },
        moveToNextPage(){
            this.page_index++;
            this.getItemInterval();
        },
        exploreData(data){
            if (this.if_chart_filter_applied){
                this.if_chart_filter_applied = false;
                this.chart_filter_label = null;
                this.$emit('exploreChart', {
                    chart_selected: null,
                    chart_filters: []
                });
            } else {
                this.if_chart_filter_applied = true;
                this.chart_filter_label = data.label;
                const selectedDimensionObj = this.original_dimensions.find(d => d.text === this.selected_dimension);
                const filter_array = [{
                    name: selectedDimensionObj.text,
                    column: selectedDimensionObj.value,
                    value: data.label
                }];
                this.$emit('exploreChart', {
                    chart_selected: this.chart_selected,
                    chart_filters: filter_array
                });
                const currentIndex = this.original_dimensions.findIndex(d => d.text === this.selected_dimension);
                const nextIndex = currentIndex >= 0 && currentIndex < this.original_dimensions.length - 1 ? currentIndex + 1 : -1;
                if (nextIndex >= 0) {
                    this.fetchDimensionWithFilter(this.original_dimensions[nextIndex], selectedDimensionObj, data.label);
                }
            }
        },
        reDrillIfNeeded() {
            if (this.is_loading_dimension) {
                return false;
            }
            const isFromThisChart = this.id == this.applied_chart_filter?.section_chart_id;
            if (!isFromThisChart || !this.applied_chart_filter?.if_chart_filter_applied) {
                return false;
            }
            const filters = this.applied_chart_filter.chart_filters;
            if (!filters || filters.length === 0) {
                return false;
            }
            const filterColumn = filters[0].column;
            const filterValue = filters[0].value;
            if (this.chart_selected.group_by?.[0]?.value !== filterColumn) {
                return false;
            }
            const filteredDimIndex = this.original_dimensions.findIndex(d => d.value === filterColumn);
            const nextIndex = filteredDimIndex + 1;
            if (filteredDimIndex < 0 || nextIndex >= this.original_dimensions.length) {
                return false;
            }
            this.fetchDimensionWithFilter(
                this.original_dimensions[nextIndex],
                this.original_dimensions[filteredDimIndex],
                filterValue
            );
            return true;
        },
        fetchDimensionWithFilter(nextDimensionObj, filteredDimensionObj, filterValue) {
            this.is_loading_dimension = true;
            this.selected_dimension = nextDimensionObj.text;
            const baseFilter = Array.isArray(this.chart_selected.filter) ? this.chart_selected.filter : [];
            const rowFilterFormula = this.buildRowFilterFormula(filteredDimensionObj, filterValue);
            const temp_filters = rowFilterFormula ? [{ main_category: 'Temp Filters', formula: rowFilterFormula }] : [];
            const chartPayload = {
                ...this.chart_selected,
                group_by: [nextDimensionObj],
                filter: baseFilter,
                temp_filters
            };
            const updateText = (arr) => {
                if (arr && arr.length > 0) {
                    for (let i = 0; i < arr.length; i++) {
                        if (arr[i].display_name) {
                            arr[i].text = arr[i].display_name;
                        } else if (arr[i].value && typeof arr[i].value === 'string') {
                            const split = arr[i].value.split('.');
                            const value = String(split[split.length - 1]).replaceAll('_', ' ');
                            arr[i].text = value.charAt(0).toUpperCase() + value.slice(1);
                        }
                    }
                }
            };
            updateText(chartPayload.group_by);
            updateText(chartPayload.metrics);
            if (this.shouldBypassChartCache()) {
                chartPayload.bypass_cache = true;
            }
            window.axios.post('/api/chart/get_data', chartPayload)
                .then(response => {
                    this.chart_selected.data = response.data.data;
                    this.chart_selected.labels = response.data.labels;
                    this.chart_selected.metric_formats = response.data.formats;
                    this.chart_selected.group_by = [nextDimensionObj];
                    this.loadTableData();
                    this.applyCurrentSort();
                    this.is_loading_dimension = false;
                })
                .catch(error => {
                    console.error('Error fetching next dimension data:', error);
                    this.is_loading_dimension = false;
                });
        },
        buildRowFilterFormula(dimensionObj, value) {
            const escaped = String(value).replace(/'/g, "''");
            if (dimensionObj.formula) {
                return dimensionObj.formula + " = '" + escaped + "'";
            }
            if (typeof dimensionObj.value === 'string' && dimensionObj.value.length > 0) {
                const col = dimensionObj.value.includes('.') ? dimensionObj.value : 'c.' + dimensionObj.value;
                return col + " = '" + escaped + "'";
            }
            return null;
        },
        shouldBypassChartCache() {
            try {
                const params = new URLSearchParams(window.location.search);
                return params.get('bypass_chart_cache') === '1' || params.get('bypass_chart_cache') === 'true';
            } catch (_) {
                return false;
            }
        },
        exportToCSV() {
            // Get the data to export (use all data, not just the current page)
            const dataToExport = this.table_data;

            if (!dataToExport || dataToExport.length === 0) {
                console.error('No data to export');
                return;
            }

            // Get headers from the first data object, excluding internal fields; use current dimension name for first column
            const rawHeaders = Object.keys(dataToExport[0]).filter(key => key !== '_rowId');
            const headers = rawHeaders.map(h => (h === 'label' ? (this.selected_dimension || h) : h));

            // Convert data to CSV format
            let csvContent = this.convertToCSV(dataToExport, rawHeaders, headers);

            // Create a file name based on the current date/time
            const fileName = `table_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;

            // Trigger download
            this.downloadCSV(csvContent, fileName);
        },
        convertToCSV(data, dataKeys, displayHeaders) {
            const headers = displayHeaders || dataKeys;
            // Create CSV header row
            let csvRows = [this.escapeCsvRow(headers)];

            // Create CSV data rows
            for (const row of data) {
                const values = dataKeys.map((key) => {
                    const value = row[key];
                    return this.formatValueForCSV(value, key);
                });
                csvRows.push(values.join(','));
            }

            return csvRows.join('\n');
        },
        escapeCsvRow(cells) {
            return cells.map(c => this.escapeCsvCell(c)).join(',');
        },
        escapeCsvCell(value) {
            const stringValue = String(value ?? '');
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return '"' + stringValue.replace(/"/g, '""') + '"';
            }
            return stringValue;
        },
        formatValueForCSV(value, key) {
            if (value === null || value === undefined) {
                return '(not set)';
            }

            // Apply the same formatting as in the table
            let formattedValue = value;
            if (key !== 'label') {
                formattedValue = this.getFormattedValueWithoutHTML(value, key);
            }

            // Escape quotes and wrap in quotes if the value contains commas or quotes
            const stringValue = String(formattedValue);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
            }

            return stringValue;
        },
        getFormattedValueWithoutHTML(value, key) {
            if (key === '_rowId' || key === 'label') {
                return value;
            }

            if (!this.chart_selected.metrics || !this.chart_selected.group_by) {
                return value;
            }

            var index = this.chart_selected.metrics.findIndex(m => m.text == key);
            if (index == -1) {
                index = this.chart_selected.group_by.findIndex(m => m.text == key) - 1;
            } else if (this.chart_selected.type !== 'multi_dimension_table') {
                index += this.chart_selected.group_by.length - 1;
            }

            if (index < 0 || index >= this.format_type.length) {
                return value;
            }
            if (this.format_type[index] == 'money') {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2
                }).format(value);
            } else if (this.format_type[index] == 'percent') {
                return new Intl.NumberFormat('en-US', {
                    style: 'percent',
                    minimumFractionDigits: 2
                }).format(value / 100);
            } else if (this.format_type[index] == 'number') {
                const isInteger = Number.isInteger(Number(value));

                return new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: isInteger ? 0 : 2,
                    maximumFractionDigits: isInteger ? 0 : 2
                }).format(value);
            } else if (this.format_type[index] == 'date') {
                return new Date(value).toLocaleDateString();
            }

            return value;
        },
        downloadCSV(csvContent, fileName) {
            // Create a Blob with the CSV content
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

            // Create a download link
            const link = document.createElement('a');

            // Create a URL for the blob
            const url = URL.createObjectURL(blob);

            // Set link properties
            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            link.style.visibility = 'hidden';

            // Add link to document, click it, and remove it
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL object
            URL.revokeObjectURL(url);
        }
    }
});
</script>
